import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { playerAvatar, promotionAvatar } from "../lib/api";
import AvatarLayered from "../components/AvatarLayered";
import AvatarHintTooltip from "../components/AvatarHintTooltip";
import { ChampionCrown } from "../components/ChampionCrown";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Camera, Users, Crown, LogOut, Trash2, UserPlus, Check, X, ShieldPlus, Send, Shield, Trophy, Swords, Mail } from "lucide-react";
import { GradCard, SectionHeading } from "../components/wrestler/WrestlerUI";
import { PrestigeBackdrop } from "../components/leaderboard/LeaderboardUI";
import { SpinFrame, HeroStat, CapacityBar, NeonBtn, IconBtn, PR_CYAN, PR_GOLD } from "../components/promotion/PromotionUI";
import { PromoXpBar, PromoUpgrades, PromoDailyBadges, PromoLevelBadge } from "../components/promotion/PromotionProgress";

/* ============================================================
   Promotion Page — three modes:
   - No pid: view your own promotion (or Found/Invitations UI)
   - :pid = your own promotion: full management view
   - :pid = someone else's promotion: read-only public view
   ============================================================ */

function Header({ p, isLeader, canManageAvatar, onChangeAvatar, uploading, onDisband, onOpenInvite, onLeave, canLeave, isMember, viewerLoggedIn, canInvite, canRequest, requested, onRequest, onCancelRequest, onRefresh }) {
  const fileRef = useRef(null);
  const champ = !!p.pw_champion;
  const frame = champ ? PR_GOLD : PR_CYAN;
  return (
    <GradCard delay={0} className="mb-5" testId="promotion-hero" bg="/backgroundgraphics/Promotion%20Page%20Banner.webp">
      {isMember && viewerLoggedIn && <PromoDailyBadges p={p} onRefresh={onRefresh} />}
      <div className="pointer-events-none absolute -inset-4 wr-spot opacity-40" style={{ background: `radial-gradient(circle at 15% 40%, ${frame}33, transparent 45%)` }} />
      <div className="relative flex flex-col lg:flex-row items-center lg:items-stretch gap-6">
        <div className="relative shrink-0 pt-4 lg:self-start">
          {champ && <ChampionCrown className="-top-3 left-1/2 -translate-x-1/2 w-12 h-12 z-20" testId="promotion-champion-crown" />}
          <SpinFrame color={frame} alt={champ ? "#FF007F" : "#9D00FF"} className="w-44 h-44">
            <div className={`relative w-full h-full ${canManageAvatar ? "cursor-pointer group" : ""}`}
                 onClick={() => canManageAvatar && fileRef.current?.click()} data-testid="promotion-avatar-wrap">
              <img src={promotionAvatar(p.promotion_id, p.avatar_updated_at)} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
              {canManageAvatar && (
                <AvatarHintTooltip side="bottom" className="absolute inset-0">
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera size={30} className="text-[#00F0FF] mb-1" />
                    <span className="text-[10px] uppercase tracking-widest font-mono text-white">{uploading ? "UPLOADING..." : "CHANGE LOGO"}</span>
                  </div>
                </AvatarHintTooltip>
              )}
            </div>
          </SpinFrame>
          <PromoLevelBadge level={p.level || 1} />
          {canManageAvatar && (
            <input ref={fileRef} data-testid="promotion-avatar-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                   onChange={(e) => onChangeAvatar(e.target.files?.[0])} />
          )}
        </div>

        <div className="flex-1 min-w-0 w-full flex flex-col gap-3 text-center lg:text-left">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#C9BDF0] mb-1 flex items-center gap-1.5 justify-center lg:justify-start">
              <Shield size={12} className="text-[#00F0FF]" /> Promotion · Est. {new Date(p.created_at).toLocaleDateString()}
            </div>
            <h1 className="font-graffiti text-4xl sm:text-5xl lg:text-6xl leading-none break-words" data-testid="promotion-name"
                style={{ color: "#fff", textShadow: `0 0 22px ${frame}AA, 0 0 44px #FF007F55, 3px 3px 0 #000` }}>{p.name}</h1>
            {champ && (
              <div data-testid="promotion-champion-chip" className="pr-champ-chip mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-heading text-[10px] uppercase tracking-widest border-2"
                   style={{ color: PR_GOLD, borderColor: PR_GOLD, background: "#0A051ACC" }}>
                <Crown size={12} fill={PR_GOLD} /> Reigning Promotion Wars Champion
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-2.5">
            <HeroStat icon={Users} label="Members" color="#FFE600" testId="promotion-member-count" value={`${p.members.length}/${p.member_cap}`} delay={80} />
            <HeroStat icon={Trophy} label="HOF" color="#FF007F" testId="promotion-hof-points" value={(p.hof_points || 0).toLocaleString()} delay={160} />
            <HeroStat icon={Swords} label="Wars Won" color={PR_GOLD} testId="promotion-pw-wins" value={p.pw_wins || 0} delay={240} />
            <PromoUpgrades p={p} isLeader={isLeader} onRefresh={onRefresh} />
          </div>
          <PromoXpBar p={p} />
          <CapacityBar count={p.members.length} cap={p.member_cap} />
        </div>

        <div className="flex flex-row flex-wrap lg:flex-col justify-center gap-2 shrink-0 lg:justify-center">
          {canRequest && (requested ? (
            <div className="flex flex-col gap-1 items-stretch">
              <div data-testid="promotion-join-requested" className="text-center text-xs px-4 py-2.5 rounded-xl border-2 font-heading uppercase tracking-widest flex items-center justify-center gap-2"
                   style={{ color: "#39FF14", borderColor: "#39FF14", background: "#0A051ACC", boxShadow: "0 0 14px #39FF1455" }}>
                <Check size={14} /> Request Sent
              </div>
              <button data-testid="promotion-join-cancel-btn" onClick={onCancelRequest} className="text-[10px] font-mono text-[#A89BCC] hover:text-[#FF007F] underline">Cancel request</button>
            </div>
          ) : (
            <NeonBtn testId="promotion-join-request-btn" color="#FF007F" icon={UserPlus} onClick={onRequest} disabled={p.members.length >= p.member_cap}
                     title="Only one open request at a time — a new request replaces your previous one">
              {p.members.length >= p.member_cap ? "Promotion Full" : "Request to Join"}
            </NeonBtn>
          ))}
          {isLeader && canInvite && <NeonBtn testId="promotion-invite-open-btn" color={PR_CYAN} icon={UserPlus} onClick={onOpenInvite}>Invite</NeonBtn>}
          {isLeader && <NeonBtn testId="promotion-disband-btn" color="#FF007F" icon={Trash2} onClick={onDisband}>Disband</NeonBtn>}
          {isMember && !isLeader && viewerLoggedIn && <NeonBtn testId="promotion-leave-btn" color="#FF9500" icon={LogOut} onClick={onLeave} disabled={!canLeave}>Leave</NeonBtn>}
        </div>
      </div>
    </GradCard>
  );
}

/* Big avatar-focused member card */
function MemberCard({ m, promo, isSelf, isLeaderViewing, onTransfer, onKick, delay }) {
  const nav = useNavigate();
  const color = m.is_leader ? PR_GOLD : isSelf ? "#39FF14" : "#FF007F";
  const open = () => m.account_name && nav(`/profile/${encodeURIComponent(m.account_name)}`);
  return (
    <div data-testid={`promotion-member-${m.user_id}`} className="pr-member wr-rise group relative rounded-2xl p-[2px] transition-transform duration-200 hover:-translate-y-1.5"
         style={{ background: `linear-gradient(150deg, ${color}, #5B3BFF66, ${color}88)`, "--pr-glow": color, animationDelay: `${delay}ms` }}>
      <div className="relative h-full rounded-[14px] bg-[#110726] p-2.5 overflow-hidden">
        <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 w-40 h-24 rounded-full blur-2xl opacity-30 transition-opacity duration-300 group-hover:opacity-60" style={{ background: color }} />
        <button data-testid={`promotion-member-avatar-${m.user_id}`} onClick={open}
                className="block relative w-full aspect-square rounded-xl overflow-hidden border-2 bg-[#0A051A]"
                style={{ borderColor: `${color}AA` }} title={m.account_name ? `View ${m.account_name}'s profile` : ""}>
          <AvatarLayered fgSrc={playerAvatar(m.account_name, m.player_avatar_updated_at)} promo={promo} alt={m.account_name}
                         className="w-full h-full transition-transform duration-500 group-hover:scale-110" />
          <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(10,5,26,0.85))" }} />
          <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100 group-hover:animate-[wrTileShine_0.8s_ease-out]" />
          {m.is_leader && (
            <div data-testid={`promotion-leader-badge-${m.user_id}`}
                 className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full font-heading text-[9px] uppercase tracking-widest flex items-center gap-1"
                 style={{ background: "#0A051ACC", border: `1px solid ${PR_GOLD}`, color: PR_GOLD, boxShadow: `0 0 10px ${PR_GOLD}77` }}>
              <Crown size={11} className="wr-crown" fill={PR_GOLD} /> Leader
            </div>
          )}
          {isSelf && (
            <div className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full text-[9px] font-heading uppercase tracking-widest text-[#0A051A]"
                 style={{ background: "#39FF14", boxShadow: "0 0 10px #39FF14" }}>You</div>
          )}
          <div className="absolute bottom-1.5 right-1.5 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur border text-[10px] font-mono flex items-center gap-1"
               style={{ borderColor: `${PR_CYAN}66`, color: "#C9BDF0" }}>
            LVL <span className="lb-num font-bold text-sm text-[#00F0FF]">{m.combined_level}</span>
          </div>
        </button>
        <button data-testid={`promotion-member-name-${m.user_id}`} onClick={open}
                className="relative mt-2 font-graffiti text-base hover:underline underline-offset-2 truncate block w-full text-center"
                style={{ color: m.is_leader ? PR_GOLD : "#fff", textShadow: `0 0 10px ${color}88, 2px 2px 0 #000` }}>
          {m.account_name}
        </button>
        {isLeaderViewing && !m.is_leader && (
          <div className="relative mt-2 flex gap-1.5">
            <button data-testid={`promotion-transfer-${m.user_id}`} onClick={() => onTransfer(m)}
                    className="flex-1 px-2 py-1.5 rounded-lg text-[9px] uppercase tracking-widest font-heading border-2 border-[#FFD700]/60 text-[#FFD700] bg-[#FFD700]/10 hover:bg-[#FFD700]/25 transition-colors">Promote</button>
            <button data-testid={`promotion-kick-${m.user_id}`} onClick={() => onKick(m)}
                    className="flex-1 px-2 py-1.5 rounded-lg text-[9px] uppercase tracking-widest font-heading border-2 border-[#FF007F]/60 text-[#FF007F] bg-[#FF007F]/10 hover:bg-[#FF007F]/25 transition-colors">Kick</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptySeat({ delay, idx }) {
  return (
    <div className="wr-rise rounded-2xl border-2 border-dashed border-[#4A3470] bg-[#0A051A]/50 flex flex-col items-center justify-center gap-1 min-h-[180px] text-[#695D8E]"
         style={{ animationDelay: `${delay}ms` }} data-testid={`promotion-empty-seat-${idx}`}>
      <UserPlus size={26} strokeWidth={2} />
      <span className="text-[10px] font-mono uppercase tracking-[0.25em]">Open Seat</span>
    </div>
  );
}

function InviteRow({ inv, onAccept, onDecline }) {
  return (
    <div data-testid={`invite-row-${inv.promotion_id}`}
         className="wr-rise group flex items-center gap-3 px-3 py-3 rounded-xl bg-[#0A051A]/85 border-2 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5"
         style={{ borderColor: "#FFE60088", boxShadow: "0 0 14px #FFE60033" }}>
      <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#0A051A] border-2 shrink-0" style={{ borderColor: "#FFE600" }}>
        <img src={promotionAvatar(inv.promotion_id, inv.avatar_updated_at)} alt={inv.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-graffiti text-lg truncate text-white" style={{ textShadow: "0 0 10px #FFE60066" }}>{inv.name}</div>
        <div className="text-[10px] font-mono text-[#A89BCC]"><span className="lb-num font-bold text-[#FFE600]">{inv.member_count}/{inv.member_cap}</span> members</div>
      </div>
      <IconBtn testId={`invite-accept-${inv.promotion_id}`} color="#39FF14" icon={Check} onClick={() => onAccept(inv)} />
      <IconBtn testId={`invite-decline-${inv.promotion_id}`} color="#FF007F" icon={X} onClick={() => onDecline(inv)} />
    </div>
  );
}

function JoinRequestsPanel({ requests, full, onReview }) {
  return (
    <GradCard delay={120} className="mb-5" testId="promotion-join-requests">
      <SectionHeading icon={UserPlus} color="#39FF14" small>Join Requests ({requests.length})</SectionHeading>
      <div className="flex flex-col gap-2">
        {requests.map((r) => (
          <div key={r.user_id} data-testid={`join-request-${r.user_id}`} className="flex items-center gap-3 p-2 rounded-xl bg-[#0A051A]/85 border-2 border-[#39FF14]/30 hover:border-[#39FF14]/70 transition-colors">
            <img src={playerAvatar(r.account_name)} alt="" className="w-11 h-11 rounded-lg object-cover border-2 border-[#39FF14]/60" />
            <div className="flex-1 min-w-0">
              <div className="font-graffiti text-base truncate text-white">{r.account_name}</div>
              <div className="text-[10px] font-mono text-[#A89BCC]">{new Date(r.requested_at).toLocaleString()}</div>
            </div>
            <IconBtn testId={`join-request-accept-${r.user_id}`} color="#39FF14" icon={Check} onClick={() => onReview(r, "accept")} disabled={full} title={full ? "Promotion is full" : "Accept"} />
            <IconBtn testId={`join-request-decline-${r.user_id}`} color="#FF007F" icon={X} onClick={() => onReview(r, "decline")} />
          </div>
        ))}
      </div>
    </GradCard>
  );
}

function PromotionView({ p, viewer, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const isMember = viewer && p.members.some((m) => m.user_id === viewer.user_id);
  const isLeader = viewer && p.leader_id === viewer.user_id;
  const canLeave = !isLeader || p.members.length === 1;
  const canInvite = isLeader && p.members.length < p.member_cap;
  const canRequest = !!viewer && !isMember && !viewer.promotion;
  const requested = (p.join_requests || []).some((r) => r.user_id === viewer?.user_id);

  const requestJoin = async () => {
    try {
      await api.post(`/promotions/${p.promotion_id}/join-request`);
      toast.success(`Request sent to ${p.name}`);
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancelRequest = async () => {
    try {
      await api.delete(`/promotions/${p.promotion_id}/join-request`);
      toast.success("Request cancelled");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reviewRequest = async (r, action) => {
    try {
      await api.post(`/promotions/${p.promotion_id}/join-requests/${r.user_id}/${action}`);
      toast.success(action === "accept" ? `${r.account_name} joined the promotion!` : "Request declined");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeAvatar = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("File must be under 3 MB");
    if (!file.type.startsWith("image/")) return toast.error("Please pick an image");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/promotions/${p.promotion_id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onRefresh();
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const invite = async () => {
    const name = inviteName.trim();
    if (!name) return;
    try {
      await api.post(`/promotions/${p.promotion_id}/invite`, { account_name: name });
      toast.success(`Invited ${name}`);
      setInviteName("");
      setInviteOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const transfer = async (m) => {
    if (!window.confirm(`Transfer leadership to ${m.account_name}? You will become a regular member.`)) return;
    try {
      await api.post(`/promotions/${p.promotion_id}/transfer`, { user_id: m.user_id });
      toast.success("Leadership transferred");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const kick = async (m) => {
    if (!window.confirm(`Kick ${m.account_name} from the promotion?`)) return;
    try {
      await api.post(`/promotions/${p.promotion_id}/kick`, { user_id: m.user_id });
      toast.success(`${m.account_name} kicked`);
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const leave = async () => {
    if (!window.confirm("Leave this promotion?")) return;
    try {
      await api.post(`/promotions/${p.promotion_id}/leave`);
      toast.success("You left the promotion");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const disband = async () => {
    if (!window.confirm("Disband the promotion? This cannot be undone.")) return;
    try {
      await api.post(`/promotions/${p.promotion_id}/disband`);
      toast.success("Promotion disbanded");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="relative max-w-[1600px] mx-auto px-4 py-6" data-testid="promotion-page">
      <PrestigeBackdrop />
      <div className="relative">
      <Header
        p={p}
        isLeader={isLeader}
        canManageAvatar={isLeader}
        onChangeAvatar={changeAvatar}
        uploading={uploading}
        onDisband={disband}
        onOpenInvite={() => setInviteOpen(true)}
        onLeave={leave}
        canLeave={canLeave}
        isMember={isMember}
        viewerLoggedIn={!!viewer}
        canInvite={canInvite}
        canRequest={canRequest}
        requested={requested}
        onRequest={requestJoin}
        onCancelRequest={cancelRequest}
        onRefresh={onRefresh}
      />

      {isLeader && (p.join_requests || []).length > 0 && (
        <JoinRequestsPanel requests={p.join_requests} full={p.members.length >= p.member_cap} onReview={reviewRequest} />
      )}

      {inviteOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="promotion-invite-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setInviteOpen(false); }}
        >
          <div className="wr-rise max-w-md w-full rounded-2xl p-[2px]" style={{ background: "linear-gradient(140deg, #00F0FF, #5B3BFF, #FF007F)", boxShadow: "0 0 40px #00F0FF55" }}>
          <div className="rounded-[14px] bg-[#110726] p-6">
            <div className="flex items-center justify-between mb-2">
              <SectionHeading icon={UserPlus} color={PR_CYAN} small className="mb-0 flex-1">Invite a Player</SectionHeading>
              <button
                data-testid="promotion-invite-close"
                onClick={() => setInviteOpen(false)}
                className="text-[#A89BCC] hover:text-[#FF007F]"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-[11px] font-mono text-[#A89BCC] mb-4">
              Type the player's exact account name. They must not be in another promotion.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                autoFocus
                data-testid="promotion-invite-input"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") invite(); }}
                placeholder="Exact account name"
                maxLength={40}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-xl bg-[#0A051A] border-2 border-[#00F0FF]/40 focus:border-[#00F0FF] outline-none font-heading text-sm"
              />
              <button
                data-testid="promotion-invite-send"
                onClick={invite}
                className="btn-neon px-5 py-2.5 text-xs flex items-center justify-center gap-2 shrink-0"
              >
                <Send size={14} /> Send Invite
              </button>
            </div>
            <div className="text-[10px] font-mono text-[#695D8E] mt-2">
              {p.members.length}/{p.member_cap} members
            </div>
          </div>
          </div>
        </div>
      )}

      <GradCard delay={200} testId="promotion-members-card">
      <SectionHeading icon={Users} color={PR_CYAN}>The Roster</SectionHeading>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {p.members.map((m, i) => (
          <MemberCard
            delay={250 + i * 70}
            key={m.user_id}
            m={m}
            promo={{ id: p.promotion_id, avatar_updated_at: p.avatar_updated_at }}
            isSelf={viewer && m.user_id === viewer.user_id}
            isLeaderViewing={isLeader}
            onTransfer={transfer}
            onKick={kick}
          />
        ))}
        {Array.from({ length: Math.min(1, Math.max(0, p.member_cap - p.members.length)) }).map((_, i) => <EmptySeat key={`e${i}`} idx={i} delay={250 + (p.members.length + i) * 70} />)}
      </div>
      </GradCard>
      </div>
    </div>
  );
}

function CreateAndInvitesView({ data, onRefresh }) {
  const [createName, setCreateName] = useState("");
  const create = async () => {
    const name = createName.trim();
    if (name.length < 3) return toast.error("Name must be at least 3 chars");
    try {
      await api.post("/promotions", { name });
      toast.success("Promotion founded!");
      setCreateName("");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const accept = async (inv) => {
    try {
      await api.post(`/promotions/${inv.promotion_id}/accept`);
      toast.success(`Joined ${inv.name}!`);
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decline = async (inv) => {
    try {
      await api.post(`/promotions/${inv.promotion_id}/decline`);
      toast.success("Invite declined");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="relative max-w-3xl mx-auto px-4 py-6" data-testid="promotion-page-empty">
      <PrestigeBackdrop />
      <div className="relative">
      <GradCard delay={0} className="mb-5" bg="/backgroundgraphics/Promotion%20Page%20Banner.webp" testId="promotion-found-card">
        <SectionHeading icon={ShieldPlus} color={PR_CYAN}>Found a Promotion</SectionHeading>
        <p className="text-xs font-mono text-[#C9BDF0] mb-4">
          Rally a stable of managers. You'll be the founder — invite friends, share a logo, and stack shows together.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="promotion-create-input"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="Promotion name (3-30 chars)"
            maxLength={30}
            className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-[#0A051A]/90 border-2 border-[#00F0FF]/40 focus:border-[#00F0FF] focus:shadow-[0_0_16px_#00F0FF66] outline-none font-graffiti text-lg uppercase transition-[border-color,box-shadow]"
          />
          <NeonBtn testId="promotion-create-submit" color="#FF007F" icon={ShieldPlus} onClick={create}>Found</NeonBtn>
        </div>
      </GradCard>

      <GradCard delay={120} testId="promotion-invitations-card">
        <SectionHeading icon={Mail} color="#FFE600" small>Invitations ({data?.invitations?.length || 0})</SectionHeading>
        {(!data?.invitations || data.invitations.length === 0) ? (
          <div className="p-6 text-center text-[#A89BCC] font-mono text-sm rounded-xl border-2 border-dashed border-[#4A3470]" data-testid="no-invitations">
            No pending invitations. Ask a friend to invite you by your exact account name.
          </div>
        ) : (
          <div className="space-y-2">
            {data.invitations.map((inv) => (
              <InviteRow key={inv.promotion_id} inv={inv} onAccept={accept} onDecline={decline} />
            ))}
          </div>
        )}
      </GradCard>
      </div>
    </div>
  );
}

export default function PromotionPage() {
  const { pid } = useParams();
  const { user, refresh: refreshUser } = useAuth();
  const [data, setData] = useState(null);       // { promotion, invitations } from /me/promotion (no pid)
  const [viewed, setViewed] = useState(null);   // public promotion (with pid)
  const [loading, setLoading] = useState(true);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (pid) {
        const r = await api.get(`/promotions/${encodeURIComponent(pid)}`);
        setViewed(r.data);
      } else {
        const r = await api.get(`/me/promotion`);
        setData(r.data);
      }
    } catch (e) {
      if (pid) {
        toast.error(e.response?.data?.detail || "Promotion not found");
        setViewed(null);
      } else {
        toast.error(e.response?.data?.detail || "Failed to load");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const onRefresh = async () => { await Promise.all([refreshUser(), load(true)]); };

  if (loading) return <div className="p-8 text-center font-graffiti text-2xl neon-cyan animate-pulse">LOADING PROMOTION...</div>;

  // Public view of any promotion
  if (pid) {
    if (!viewed) return <div className="p-8 text-center font-heading neon-pink">PROMOTION NOT FOUND</div>;
    return <PromotionView p={viewed} viewer={user} onRefresh={onRefresh} />;
  }

  // Self view — either my promotion or the create/invitations screen
  if (data?.promotion) return <PromotionView p={data.promotion} viewer={user} onRefresh={onRefresh} />;
  return <CreateAndInvitesView data={data} onRefresh={onRefresh} />;
}
